import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Modal } from '@components/common/UIComponents';
import {
  getAllFoods,
  addFoodItem,
  updateFoodItem,
  deleteFoodItem,
  bulkImportFoods
} from '@services/firestoreService';
import { indianFoods } from '../data/indianFoods';
import { Plus, Search, Edit2, Trash2, Upload, Download } from 'lucide-react';

const FoodsManagement = () => {
  const [foods, setFoods] = useState([]);
  const [filteredFoods, setFilteredFoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFood, setEditingFood] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    servingSize: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    category: ''
  });

  const categories = [
    'Grains', 'Rice Dishes', 'Breads', 'Dal', 'Vegetables', 'Non-Veg',
    'Snacks', 'South Indian', 'Sweets', 'Dairy', 'Beverages', 'Street Food',
    'Breakfast', 'Condiments', 'Other'
  ];

  useEffect(() => {
    loadFoods();
  }, []);

  useEffect(() => {
    filterFoods();
  }, [searchTerm, foods]);

  const loadFoods = async () => {
    try {
      setLoading(true);
      const allFoods = await getAllFoods();
      setFoods(allFoods);
      setFilteredFoods(allFoods);
    } catch (error) {
      console.error('Error loading foods:', error);
      alert('Error loading foods: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterFoods = () => {
    if (!searchTerm) {
      setFilteredFoods(foods);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = foods.filter(food =>
        food.name.toLowerCase().includes(searchLower) ||
        food.category?.toLowerCase().includes(searchLower)
      );
      setFilteredFoods(filtered);
    }
  };

  const handleOpenModal = (food = null) => {
    if (food) {
      setEditingFood(food);
      setFormData({
        name: food.name,
        servingSize: food.servingSize,
        calories: food.calories.toString(),
        protein: food.protein.toString(),
        carbs: food.carbs.toString(),
        fat: food.fat.toString(),
        category: food.category || ''
      });
    } else {
      setEditingFood(null);
      setFormData({
        name: '',
        servingSize: '',
        calories: '',
        protein: '',
        carbs: '',
        fat: '',
        category: ''
      });
    }
    setShowModal(true);
  };

  const handleSaveFood = async () => {
    if (!formData.name || !formData.calories) {
      alert('Please fill in at least Name and Calories');
      return;
    }

    try {
      const foodData = {
        name: formData.name.trim(),
        servingSize: formData.servingSize.trim() || '100g',
        calories: parseFloat(formData.calories),
        protein: parseFloat(formData.protein) || 0,
        carbs: parseFloat(formData.carbs) || 0,
        fat: parseFloat(formData.fat) || 0,
        category: formData.category || 'Other'
      };

      if (editingFood) {
        await updateFoodItem(editingFood.id, foodData);
      } else {
        await addFoodItem(foodData);
      }

      setShowModal(false);
      loadFoods();
    } catch (error) {
      console.error('Error saving food:', error);
      alert('Error saving food: ' + error.message);
    }
  };

  const handleDeleteFood = async (foodId, foodName) => {
    if (!window.confirm(`Are you sure you want to delete "${foodName}"?`)) {
      return;
    }

    try {
      await deleteFoodItem(foodId);
      loadFoods();
    } catch (error) {
      console.error('Error deleting food:', error);
      alert('Error deleting food: ' + error.message);
    }
  };

  const handleBulkImport = async () => {
    if (!window.confirm(`This will import ${indianFoods.length} Indian food items. Continue?`)) {
      return;
    }

    try {
      setLoading(true);
      await bulkImportFoods(indianFoods);
      alert(`Successfully imported ${indianFoods.length} food items!`);
      loadFoods();
    } catch (error) {
      console.error('Error importing foods:', error);
      alert('Error importing foods: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    const dataStr = JSON.stringify(foods, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'foods-database.json';
    link.click();
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Foods Database Management</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your food items database for easy meal tracking
        </p>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search foods by name or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={() => handleOpenModal()}>
            <Plus size={20} className="mr-2" />
            Add Food
          </Button>
          <Button onClick={handleBulkImport} variant="outline">
            <Upload size={20} className="mr-2" />
            Import Indian Foods
          </Button>
          <Button onClick={handleExportData} variant="outline">
            <Download size={20} className="mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Foods</p>
          <p className="text-2xl font-bold">{foods.length}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400">Search Results</p>
          <p className="text-2xl font-bold">{filteredFoods.length}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400">Categories</p>
          <p className="text-2xl font-bold">
            {[...new Set(foods.map(f => f.category))].length}
          </p>
        </Card>
      </div>

      {/* Foods Table */}
      <Card>
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading foods...</p>
          </div>
        ) : filteredFoods.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {foods.length === 0 ? 'No foods in database yet' : 'No foods match your search'}
            </p>
            {foods.length === 0 && (
              <Button onClick={handleBulkImport}>
                <Upload size={20} className="mr-2" />
                Import Indian Foods Database
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Food Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Serving Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Calories
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Protein (g)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Carbs (g)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Fat (g)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Category
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredFoods.map((food) => (
                  <tr key={food.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {food.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {food.servingSize}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {food.calories}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {food.protein}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {food.carbs}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {food.fat}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300">
                        {food.category || 'Other'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(food)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteFood(food.id, food.name)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingFood ? 'Edit Food Item' : 'Add New Food Item'}
      >
        <div className="space-y-4">
          <Input
            label="Food Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Paneer Tikka"
            fullWidth
          />

          <Input
            label="Serving Size"
            value={formData.servingSize}
            onChange={(e) => setFormData({ ...formData, servingSize: e.target.value })}
            placeholder="e.g., 1 cup (200g)"
            fullWidth
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Calories *"
              type="number"
              value={formData.calories}
              onChange={(e) => setFormData({ ...formData, calories: e.target.value })}
              placeholder="250"
              fullWidth
            />

            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Protein (g)"
              type="number"
              step="0.1"
              value={formData.protein}
              onChange={(e) => setFormData({ ...formData, protein: e.target.value })}
              placeholder="0"
              fullWidth
            />

            <Input
              label="Carbs (g)"
              type="number"
              step="0.1"
              value={formData.carbs}
              onChange={(e) => setFormData({ ...formData, carbs: e.target.value })}
              placeholder="0"
              fullWidth
            />

            <Input
              label="Fat (g)"
              type="number"
              step="0.1"
              value={formData.fat}
              onChange={(e) => setFormData({ ...formData, fat: e.target.value })}
              placeholder="0"
              fullWidth
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleSaveFood} fullWidth>
              {editingFood ? 'Update Food' : 'Add Food'}
            </Button>
            <Button
              onClick={() => setShowModal(false)}
              variant="outline"
              fullWidth
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FoodsManagement;
